import React, { useRef, useEffect } from 'react';
import Button from '@atlaskit/button';
import Form, { Field, ErrorMessage } from '@atlaskit/form';
import {
	ModalTransition,
	ModalBody,
	ModalHeader,
	ModalTitle,
	ModalFooter,
} from '@atlaskit/modal-dialog';
import ShortcutScope from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcut-scope.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useFlagsService } from '@atlassian/jira-flags';
import { useIntl } from '@atlassian/jira-intl';
import { AttachmentsField as AttachmentsFieldBase } from '@atlassian/jira-issue-create-commons/src/ui/attachments-field/index.tsx';
import { AsyncRichTextEditView } from '@atlassian/jira-issue-field-rich-text/src/async.tsx';
import { JiraModal as Modal } from '@atlassian/jira-modal/src/ui/jira-modal.tsx';
import { OPERATION_IDS } from '@atlassian/jira-issue-operations-commons/src/constants.tsx';
import { useIssueOperationsDialogContext } from '@atlassian/jira-issue-operations-dialog-context-provider/src/index.tsx';
import { UserMentionProvider } from '@atlassian/jira-mention-provider/src/services/user-mention-provider/index.tsx';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import useAttachFile from '../services/attach-files/index.tsx';
import messages from './messages.tsx';

export const AttachFileDialog = (): React.JSX.Element => {
	const [{ openDialog, mediaUploadContext, issueId, issueKey, workType }, { setDialogContext }] =
		useIssueOperationsDialogContext();
	const { attachFilesQuery } = useAttachFile();
	const { formatMessage } = useIntl();
	const { showFlag } = useFlagsService();
	const accountId = useAccountId();

	const mentionProvider = useRef(new UserMentionProvider(issueKey || '', accountId));

	useEffect(() => {
		mentionProvider.current = new UserMentionProvider(issueKey || '', accountId);
	}, [accountId, issueKey]);

	const isOpen = openDialog === OPERATION_IDS.ATTACH_FILE;
	const onClose = () => setDialogContext(null, null, null, null);

	const successTitle = (() => {
		if (workType && expVal('jira-itr-m4-exp-3', 'isEnabled', false)) {
			return messages.issueUpdatedIssueTerminologyRefreshM4;
		}

		// If the experiment jira-itr-m4-exp-3 is not enabled or workType is not defined, we use a different message for the success title.
		return expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
			? messages.issueUpdatedIssueTermRefresh
			: messages.issueUpdated;
	})();

	const onSuccess = () => {
		showFlag({
			type: 'success',
			title: [successTitle, { workType, issueKey }],
			description: [messages.issueAttached, { issueKey }],
			messageId: 'issue-operations-attach-file-dialog.ui.show-flag.success',
			messageType: 'transactional',
		});
		onClose();
	};

	const onFailure = () => {
		showFlag({
			type: 'error',
			title: [messages.errorTitle, { issueKey }],
			description: [messages.errorDesc, { issueKey }],
			messageId: 'issue-operations-attach-file-dialog.ui.show-flag.error',
			messageType: 'transactional',
		});
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const onSubmit = (data: any) =>
		data.attachment?.length !== 0 &&
		issueId !== null &&
		attachFilesQuery(issueId, data.attachment, data.comment, onSuccess, onFailure);

	return (
		<ModalTransition>
			{isOpen && issueId !== null && issueKey !== null && mediaUploadContext !== null && (
				<ShortcutScope>
					<Modal
						onClose={onClose}
						testId="issue-operations-attach-file-dialog.ui.modal"
						messageId="issue-operations-attach-file-dialog.ui.modal"
						messageType="transactional"
					>
						<ModalHeader hasCloseButton>
							<ModalTitle>{formatMessage(messages.modalHeading, { issueKey })}</ModalTitle>
						</ModalHeader>
						<ModalBody>
							<Form onSubmit={onSubmit}>
								{({ formProps }) => (
									<form {...formProps} id="issue-operations.attach-file-modal">
										<Field
											label={formatMessage(messages.attachmentLabel)}
											name="attachment"
											validate={(value) =>
												!(value?.length !== 0)
													? formatMessage(messages.attachmentValidationWarning)
													: undefined
											}
											isRequired
										>
											{({ fieldProps, error }) => (
												<>
													<AttachmentsFieldBase
														fieldId="attachment"
														fieldName="attachment"
														// @ts-expect-error - TS2322 - Type 'FieldProps<string, HTMLInputElement>' is not assignable to type 'FieldProps<FieldValue>'.
														fieldProps={fieldProps}
														mediaUploadContext={mediaUploadContext}
														isRequired
													/>
													{error !== undefined && <ErrorMessage>{error}</ErrorMessage>}
												</>
											)}
										</Field>
										<Field label={formatMessage(messages.commentLabel)} name="comment">
											{({ fieldProps }) => (
												<AsyncRichTextEditView
													{...fieldProps}
													assistiveLabel={formatMessage(messages.commentLabel)}
													mediaContext={{
														uploadContext: mediaUploadContext,
														// @ts-expect-error - TS2740 - Type '{}' is missing the following properties from type 'ViewContext': clientId, serviceHost, token, tokenLifespanInMs, and 3 more.
														viewContext: {},
													}}
													mentionProvider={mentionProvider.current}
												/>
											)}
										</Field>
									</form>
								)}
							</Form>
						</ModalBody>
						<ModalFooter>
							<Button appearance="primary" type="submit" form="issue-operations.attach-file-modal">
								{formatMessage(messages.modalSubmit)}
							</Button>
							<Button appearance="subtle" onClick={onClose}>
								{formatMessage(messages.modalClose)}
							</Button>
						</ModalFooter>
					</Modal>
				</ShortcutScope>
			)}
		</ModalTransition>
	);
};

export default AttachFileDialog;
